import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SeoManager, seoConfig } from '../seo';
import '../styles/TypingResults.css';

// Gauge Component: Visualizes CPS on a semi-circular gauge.
function Gauge({ cps }) {
  const maxCps = 15;
  const targetAngle = Math.min((cps / maxCps) * 180, 180);
  const [needleAngle, setNeedleAngle] = useState(0);

  // Map CPS to color: red -> orange -> yellow -> light green -> dark green
  function getGaugeColor(cps) {
    if (cps < 4) return '#e53935'; // Red
    if (cps < 7) return '#fb8c00'; // Orange
    if (cps < 10) return '#fbc02d'; // Yellow
    if (cps < 12) return '#7cb342'; // Light Green
    return '#388e3c'; // Dark Green
  }

  useEffect(() => {
    const timer = setTimeout(() => setNeedleAngle(targetAngle), 100);
    return () => clearTimeout(timer);
  }, [targetAngle]);

  const gaugeColor = getGaugeColor(cps);

  return (
    <div className="gauge-outer">
      <svg className="gauge-svg" viewBox="0 0 100 50">
        <path d="M 10 50 A 40 40 0 0 1 90 50" stroke="var(--gauge-bg)" strokeWidth="8" fill="none" />
        <path 
          d="M 10 50 A 40 40 0 0 1 90 50" 
          stroke={gaugeColor}
          strokeWidth="8" 
          fill="none" 
          strokeDasharray="125.6" 
          strokeDashoffset={125.6 * (1 - Math.min(cps, maxCps) / maxCps)}
          className="gauge-progress"
        />
      </svg>
      <div
        className="gauge-needle"
        style={{ transform: `translateX(-50%) rotate(${needleAngle - 90}deg)` }}
      />
      <div className="gauge-pivot"></div>
    </div>
  );
}

// Example ranks and roasts for click speed
const clickRanks = [
  { min: 12, label: 'Cheetah', roast: 'Lightning fast! Are you even human?' },
  { min: 10, label: 'Wolf', roast: 'Incredible! You have the reflexes of a predator.' },
  { min: 7, label: 'Rabbit', roast: 'Quick and nimble! Keep going.' },
  { min: 4, label: 'Turtle', roast: 'Not bad, but you can go faster!' },
  { min: 0, label: 'Snail', roast: 'Slow and steady... but maybe too steady.' },
];

function getClickRank(cps) {
  for (const rank of clickRanks) {
    if (cps >= rank.min) return rank;
  }
  return clickRanks[clickRanks.length - 1];
}

export default function ClickTestResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const { cps = 0, clicks = 0, duration = 1 } = location.state || {};
  const rank = getClickRank(Number(cps));

  const [showTryAgainButton, setShowTryAgainButton] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTryAgainButton(true);
    }, 1000); // 1 second delay

    return () => clearTimeout(timer); // Clean up the timer
  }, []); // Empty dependency array means this effect runs once on mount

  // Determine route for try again
  const durationRoute = {
    1: '/1s-cps-click-test',
    5: '/5s-cps-click-test',
    15: '/15s-cps-click-test',
    30: '/30s-cps-click-test',
    60: '/60s-cps-click-test',
  }[duration] || '/1s-cps-click-test';

  return (
    <>
      <SeoManager
        title={seoConfig['click-test-results']?.title}
        description={seoConfig['click-test-results']?.description}
        keywords={seoConfig['click-test-results']?.keywords}
        noindex
      />
      <div className="results-bg">
        <div className="results-card">
          <h2 className="results-title">Click Test Results</h2>
          <div className="gauge-center">
            <Gauge cps={Number(cps)} />
          </div>
          <div className="rank-row">
            <span className="rank-label">Your Rank:</span>
            <span className="rank-animal">{rank.label}</span>
          </div>
          <div className="rank-message">{rank.roast}</div>
          <div className="results-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="result-item">
              <h3>CPS</h3>
              <p className="big-number">{cps}</p>
              <p className="label">Clicks per second</p>
            </div>
            <div className="result-item">
              <h3>Clicks</h3>
              <p className="big-number">{clicks}</p>
              <p className="label">Total clicks</p>
            </div>
          </div>
          {showTryAgainButton && (
            <button className="try-again-btn" onClick={() => navigate(durationRoute)}>
              Try Again
            </button>
          )}
        </div>
      </div>
    </>
  );
} 